/**
 * Paging through a long list (CO-176). One shape everywhere: the range that
 * is shown ("21–40 of 1,204") and two small secondary buttons, Previous and
 * Next. The buttons are always small and always secondary because paging is
 * navigation inside a list, never the main action of a view.
 *
 * The component derives which button is disabled from the numbers, so a call
 * site cannot disable one and forget the other:
 *
 *   <Pager first={offset + 1} last={offset + rows.length} total={total}
 *          onPrevious={() => setOffset(offset - PAGE)} onNext={() => setOffset(offset + PAGE)} />
 */
export function Pager({
  first,
  last,
  total,
  onPrevious,
  onNext,
  busy = false,
  label = 'Pages',
  className,
}: {
  /** 1-based position of the first row shown. */
  first: number;
  /** 1-based position of the last row shown. */
  last: number;
  /** How many rows there are in all. */
  total: number;
  onPrevious: () => void;
  onNext: () => void;
  /** Disables both buttons while the next page is being fetched. */
  busy?: boolean;
  /** Names the group when a view has more than one list. */
  label?: string;
  className?: string;
}) {
  const format = new Intl.NumberFormat();
  return (
    <div className={`pager${className ? ` ${className}` : ''}`} role="group" aria-label={label}>
      <p className="pager__range" aria-live="polite">
        {format.format(first)}–{format.format(last)} of {format.format(total)}
      </p>
      <div className="pager__buttons">
        <button type="button" className="button button--secondary button--small" disabled={busy || first <= 1} onClick={onPrevious}>
          Previous
        </button>
        <button type="button" className="button button--secondary button--small" disabled={busy || last >= total} onClick={onNext}>
          Next
        </button>
      </div>
    </div>
  );
}
